import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Badge, Button, EmptyState, LoadingState, ErrorState } from "@/components/design-system";
import { listMySubmissions } from "@/lib/campaigns";
import { formatApiError } from "@/lib/api";

export default function MyFeedback() {
  const [state, setState] = useState({ status: "loading", items: [], err: "" });

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const items = await listMySubmissions();
      setState({ status: "ready", items, err: "" });
    } catch (err) { setState({ status: "error", items: [], err: formatApiError(err) }); }
  };
  useEffect(() => { load(); }, []);

  const helpfulCount = state.items.filter((s) => s.helpful).length;
  const points = state.items.filter((s) => s.moderation_status !== "hidden").length * 2 + helpfulCount * 5;

  return (
    <div data-testid="my-feedback-page">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Community</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">My feedback</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Every submission earns +2 reputation. When a founder marks your answer helpful, that's +5 more. Formula is transparent — Cycle C5 will show live tier progress.</p>

      {state.status === "loading" && <LoadingState label="Loading your feedback…" />}
      {state.status === "error" && <ErrorState detail={state.err} action={<Button variant="secondary" onClick={load}>Retry</Button>} />}
      {state.status === "ready" && (
        <>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="!p-6"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Submissions</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{state.items.length}</div></Card>
            <Card className="!p-6"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Marked helpful</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{helpfulCount}</div></Card>
            <Card className="!p-6"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Reputation (preview)</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{points}</div><div className="text-xs text-[#0B1F3A]/60 mt-1">Live in Cycle C5 →</div></Card>
          </div>

          <div className="mt-8">
            {state.items.length === 0 ? (
              <EmptyState
                title="No feedback yet"
                description="Head over to Explore, pick a product you care about, and give structured feedback in an open campaign."
                action={<Link to="/app/explore"><Button variant="primary">Explore products</Button></Link>}
              />
            ) : (
              <div className="space-y-4" data-testid="my-feedback-list">
                {state.items.map((s) => (
                  <Card key={s.id} data-testid={`my-feedback-${s.id}`} className="!p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {s.helpful && <Badge variant="active">★ Helpful</Badge>}
                          {s.moderation_status === "hidden" && <Badge variant="admin">Hidden by admin</Badge>}
                          <span className="text-xs text-[#0B1F3A]/60">{new Date(s.created_at).toLocaleString()}</span>
                        </div>
                        <div className="mt-2 font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Feedback on <span className="text-[#2D7FF9]">{s.product_name}</span></div>
                        <div className="text-xs text-[#0B1F3A]/60 mt-1">{s.answers.length} answers</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
